import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { functions } from '../../firebase/firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { firebaseCache } from '../../utils/firebaseCache';

// Async thunk to fetch staff members for an admin
export const fetchStaffs = createAsyncThunk(
    'staff/fetchStaffs',
    async (adminUid, { rejectWithValue }) => {
        try {
            if (!adminUid) {
                throw new Error('Admin UID is required to fetch staff');
            }

            const cacheKey = `staffs_${adminUid}`;

            // Try cache first
            const cachedData = firebaseCache.get(cacheKey);
            if (cachedData) {
                console.log(`✅ Returning ${cachedData.length} staff members from cache (key: ${cacheKey})`);
                return cachedData;
            }

            console.log('📥 Cache miss - fetching staff list from Cloud Function');

            // Call Cloud Function
            const listStaffsForAdmin = httpsCallable(functions, 'listStaffsForAdmin');
            const result = await listStaffsForAdmin({});
            const staffs = result.data.staffs || [];

            // Cache for 24 hours
            firebaseCache.set(cacheKey, staffs, 24 * 60 * 60 * 1000);

            return staffs;
        } catch (error) {
            console.error('Error fetching staff list:', error);
            return rejectWithValue(error.message || 'Failed to fetch staff list');
        }
    }
);

const staffSlice = createSlice({
    name: 'staff',
    initialState: {
        staffs: [],
        loading: false,
        error: null,
        lastFetched: null,
    },
    reducers: {
        clearStaffCache: (state, action) => {
            const adminUid = action.payload;
            if (adminUid) {
                const cacheKey = `staffs_${adminUid}`;
                firebaseCache.clearKey(cacheKey);
                state.lastFetched = null; // Reset lastFetched when cache is cleared
            }
        },
        clearStaffs: (state) => {
            state.staffs = [];
            state.error = null;
            state.lastFetched = null; // Reset lastFetched when staffs are cleared
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchStaffs.pending, (state) => {
                // Only show loader if we have no data
                if (state.staffs.length === 0) {
                    state.loading = true;
                }
                state.error = null;
            })
            .addCase(fetchStaffs.fulfilled, (state, action) => {
                state.staffs = action.payload;
                state.loading = false;
                state.lastFetched = Date.now();
            })
            .addCase(fetchStaffs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch staff list';
            });
    },
});

export const { clearStaffCache, clearStaffs } = staffSlice.actions;
export default staffSlice.reducer;
